/**
 * emit-block — the round-trip *writer* for the block-mode editor (§5.2).
 *
 * `emitBlock(block)` regenerates the SINGLE source line for a parsed block from
 * its fields — the inverse of `parse-blocks`' `classify()` for the EDITABLE leaf
 * kinds. It underwrites the invariant that lets card mode write edits straight
 * back into the source:
 *
 *   parseLsBlocks(emitBlock(b))[0]  ≡  b   (same kind + same fields)
 *
 * for any editable leaf `b`. It emits exactly the surface syntax the display
 * parser reads (LS-SPEC §4), so a round-trip never drifts.
 *
 * Structural openers are re-emittable ONLY where a single-line patch is provably
 * safe — the `@episode … {` header, an `@if`/`@else` branch, an inline
 * `@gate { … }`, and `key: value` param lines (check / achievement metadata).
 * For these we reconstruct the surrounding braces from the original `raw` so the
 * `{`/`}` structure is never lost. Genuinely multi-line-owning kinds (choice /
 * check-opener / phone / unknown) still return `raw` verbatim.
 *
 * Design rules (mirroring parse-blocks):
 *  - Never throw. Missing fields degrade to empty tokens, never crashes.
 *  - Emit the *unindented* directive body only. Leading indentation is the
 *    caller's concern (it splices the line back preserving the original indent).
 *  - Respect `concurrent` where the directive supports the `&` follower prefix.
 */
import type { LsBlock } from "./parse-blocks.js";

/** The `@` head, or `&` when this directive is a concurrent follower. */
function head(block: { concurrent?: boolean }): "@" | "&" {
  return block.concurrent ? "&" : "@";
}

/** Join non-empty tokens with single spaces (drops undefined / "" holes). */
function tokens(...parts: Array<string | undefined>): string {
  return parts.filter((p): p is string => p != null && p !== "").join(" ");
}

/** Preserve a same-line opening brace for editable structural opener rows. */
function preserveOpeningBrace(raw: string, line: string): string {
  return raw.trim().endsWith("{") ? `${line} {` : line;
}

/**
 * Regenerate the single source line for a block.
 *
 * For editable leaf kinds this produces canonical LS syntax that re-parses to
 * an equivalent block. For every other kind it returns `block.raw` unchanged.
 */
export function emitBlock(block: LsBlock): string {
  switch (block.kind) {
    // ── dialogue / narration (no @ prefix) ──────────────────────────────
    // SPEAKER: text  |  SPEAKER [pose]: text
    case "dialogue": {
      const posed = block.pose ? `${block.speaker} [${block.pose}]` : block.speaker;
      return `${posed}: ${block.text}`;
    }
    case "narration":
      // NARRATOR: text  |  YOU: text
      return `${block.speaker}: ${block.text}`;

    // ── @bg set <name> [transition] ─────────────────────────────────────
    case "bg":
      return tokens(`${head(block)}bg set`, block.name, block.transition);

    // ── @<char> <pose> [transition]  |  @<char> bubble <type> ───────────
    case "char": {
      const h = head(block);
      if (block.bubble) {
        // @<char> bubble <type>
        return tokens(`${h}${block.character} bubble`, block.pose);
      }
      return tokens(`${h}${block.character}`, block.pose, block.transition);
    }

    // ── @cg <name> "<content>" ──────────────────────────────────────────
    case "cg": {
      const h = head(block);
      const content = block.content != null ? `"${block.content}"` : undefined;
      return tokens(`${h}cg`, block.name, content);
    }

    // ── @music <name>  |  @music stop ───────────────────────────────────
    case "music": {
      const h = head(block);
      if (block.stop) return `${h}music stop`;
      return tokens(`${h}music`, block.name);
    }

    // ── @sfx <name> ─────────────────────────────────────────────────────
    case "sfx":
      return tokens(`${head(block)}sfx`, block.name);

    // ── @pause ──────────────────────────────────────────────────────────
    case "pause":
      return `${head(block)}pause`;

    // ── @option <id> <brave|safe> "<text>" ──────────────────────────────
    case "option": {
      const text = block.text != null ? `"${block.text}"` : undefined;
      return preserveOpeningBrace(block.raw, tokens("@option", block.id, block.mode, text));
    }

    // ── @affection <character> <delta> ──────────────────────────────────
    case "affection":
      return tokens(`${head(block)}affection`, block.character, block.delta);

    // ── @signal mark <event>  |  @signal int <name> <op> <value> ────────
    case "signal": {
      const h = head(block);
      if (block.signalKind === "int") {
        // int forms: `name = 0` / `name +1` / `name -2`
        const op = block.op ?? "=";
        // `=` takes a space before the value; `+` / `-` are glued to it.
        const assign = op === "=" ? tokens("=", block.value) : `${op}${block.value ?? ""}`;
        return tokens(`${h}signal int`, block.name, assign);
      }
      // mark form (default)
      return tokens(`${h}signal mark`, block.name);
    }

    // ── @trick <type> "<prompt>" ────────────────────────────────────────
    case "trick": {
      const prompt = block.prompt != null ? `"${block.prompt}"` : undefined;
      return tokens(`${head(block)}trick`, block.trickType, prompt);
    }

    // ── @text from/to <CHAR>: content ───────────────────────────────────
    case "phoneText":
      return tokens(`${head(block)}text`, block.direction, block.character) + `: ${block.text ?? ""}`;

    // ── @minigame <name> "<description>" ────────────────────────────────
    case "minigame": {
      const desc = block.description != null ? `"${block.description}"` : undefined;
      return tokens(`${head(block)}minigame`, block.name, desc);
    }

    // ── @achievement <id> ───────────────────────────────────────────────
    case "achievement":
      return preserveOpeningBrace(block.raw, tokens("@achievement", block.id));

    // ── @butterfly "<description>" ──────────────────────────────────────
    case "butterfly": {
      const description = block.description != null ? `"${block.description}"` : undefined;
      return tokens(`${head(block)}butterfly`, description);
    }

    // ── @episode <branch_key> "<title>" { (header line; always a { opener) ─
    case "episode": {
      const title = block.title != null ? `"${block.title}"` : undefined;
      return `${tokens("@episode", block.branchKey, title)} {`;
    }

    // ── key: value metadata line (check attr/dc, achievement name/rarity/…) ─
    case "param":
      return `${block.key}: ${block.value}`;

    // ── @gate { … } — only the INLINE self-closing form is re-emittable ───
    // A bare `@gate {` opener owns a multi-line body → return raw untouched.
    case "gate": {
      if (block.raw.includes("}")) {
        if (block.next) return `@gate { @next ${block.next} }`;
        if (block.end) return `@gate { @end ${block.end} }`;
      }
      return block.raw;
    }

    // ── @if / @else branch — body opener (`… {`) or gate colon (`: @next X`) ─
    // Braces are reconstructed from `raw`: a leading `}` (`} @else {`) and a
    // trailing `{` are preserved so the block structure is never corrupted.
    case "if": {
      const rawT = block.raw.trim();
      const lead = rawT.startsWith("}") ? "} " : "";
      const kw = block.isElse ? (block.condition != null ? "@else @if" : "@else") : "@if";
      const cond = block.condition != null ? ` (${block.condition})` : "";
      if (rawT.endsWith("{")) return `${lead}${kw}${cond} {`;
      const outcome = block.next ? `@next ${block.next}` : block.end ? `@end ${block.end}` : undefined;
      if (!outcome) return block.raw; // no routing target — leave verbatim
      return `${lead}${kw}${cond}: ${outcome}`;
    }

    // ── non-editable kinds: identity (edited only in code mode) ──────────
    // choice / check-opener / phone / unknown
    default:
      return block.raw;
  }
}
